import { FunctionComponent, useEffect, useMemo, useState } from "react";

import {
	faClipboardCheck,
	faMagnifyingGlass,
	faPaperPlane,
	faSparkles,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { Icon } from "@/components";

import { classNames } from "@/utils/classNames";

import { Sender } from "@/pages/Private/pages/List/components/CampaignOverview/components/Sender";
import { SenderAvatars } from "@/pages/Private/pages/List/components/CampaignOverview/components/SenderAvatars";
import { SenderSelector } from "@/pages/Private/pages/List/components/CampaignOverview/components/SenderSelector/SenderSelector";
import { getCampaignStepChannels } from "@/pages/Private/pages/List/components/CampaignOverview/components/SenderSelector/senderSelector.helpers";
import { computeCurrentSenders } from "@/pages/Private/pages/List/components/CampaignOverview/utils/computeCurrentSenders.util";
import { useCampaignValidation } from "@/pages/Private/pages/List/components/CampaignOverview/utils/useCampaignValidation";
import { buildCampaignBuilderPath } from "@/pages/Private/pages/List/utils/campaignBuilderPath";

import { useGetSendersUsersQuery } from "@/pages/Private/redux/admin/admin.api";
import { useGetCampaignStatsQuery } from "@/pages/Private/redux/list/list.api";

import { formatReachOutCampaignStat } from "./reachOutCampaignStat";
import { deriveReachOutCampaigns, PickerCampaign } from "./reachOutCampaigns";
import {
	ReachOutTarget,
	reachOutTargetCount,
	reachOutTargetList,
	reachOutTargetListId,
} from "./reachOutTarget";
import { AlreadyConnectedNoticeView } from "./alreadyConnected/AlreadyConnectedNotice";
import { leadsToAdd } from "./alreadyConnected/deriveAlreadyConnectedNotice";
import { useAlreadyConnectedCheck } from "./alreadyConnected/useAlreadyConnectedCheck";
import { useReachOutCampaignCommit } from "./useReachOutCampaignCommit";

interface ReachOutCampaignPickerProps {
	target: ReachOutTarget;
	/** Close the whole reach-out menu after an action commits. */
	onDone: () => void;
	/**
	 * Terminal action of the picker:
	 * - `"send"` (default, "Add to campaign") — assign & send out via `bulkAssign` (assignStatus ASSIGNING).
	 * - `"assign"` ("Review & edit campaign") — pre-assign for review: set campaignId + addedToCampaignAt
	 *   WITHOUT sending, then jump to ReadyToSend.
	 */
	variant?: "send" | "assign";
	/**
	 * Reports the "Change the senders of the campaign" toggle state up to the reach-out menu, which
	 * widens itself while the reused senders panel is open (it needs more horizontal room).
	 */
	onSendersPanelChange?: (open: boolean) => void;
}

/**
 * Inline "Add to campaign" picker rendered inside the reach-out menu. Search the list's valid
 * campaigns, pick one, optionally override its senders, then choose to create AI snippets or
 * add-and-send. Works over either a single lead or a bulk table selection (see
 * {@link ReachOutTarget}); the actual mutations live in {@link useReachOutCampaignCommit}.
 */
export const ReachOutCampaignPicker: FunctionComponent<ReachOutCampaignPickerProps> = ({
	target,
	onDone,
	variant = "send",
	onSendersPanelChange,
}) => {
	const { t } = useTranslation();

	// Coming from the campaign builder (?assignCampaignUuid=…, see CampaignOverview), preselect that
	// campaign so the user only has to confirm. An unknown/absent uuid falls back to the search list.
	const [searchParams] = useSearchParams();
	const [query, setQuery] = useState("");
	const [selectedId, setSelectedId] = useState<string | null>(() =>
		searchParams.get("assignCampaignUuid")
	);
	const [changeSenders, setChangeSenders] = useState(false);
	// null until the user edits the senders panel — then holds their override. The effective senders
	// fall back to the campaign's default config merged with available accounts (`currentSenders`).
	const [overrideSenders, setOverrideSenders] = useState<Sender[] | null>(null);

	const listId = reachOutTargetListId(target);
	const list = reachOutTargetList(target);

	// Report the "Change senders" toggle up to the reach-out menu, which widens itself while the
	// reused senders panel is open (it needs more horizontal room than the compact default). Fires on
	// toggle on/off and on the resets in pickCampaign/clearSelection.
	useEffect(() => {
		onSendersPanelChange?.(changeSenders);
	}, [changeSenders, onSendersPanelChange]);

	const { data: sendingUsers } = useGetSendersUsersQuery();
	// Per-campaign engagement counts for the card's progress line. Cached by listId, so every lead
	// row's picker shares one request (mirrors ListEditCopywriting's campaign table).
	const { data: campaignStats } = useGetCampaignStatsQuery({ listId });

	// Every campaign of this target's list — invalid ones included, so the picker can show them
	// disabled with a reason instead of silently dropping them (PREC-3952).
	const campaigns = useMemo<PickerCampaign[]>(() => deriveReachOutCampaigns(list), [list]);
	const validation = useCampaignValidation(list);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();

		const matching = q ? campaigns.filter((c) => c.name?.toLowerCase().includes(q)) : campaigns;

		return [...matching].sort((a, b) => Number(b.isValid) - Number(a.isValid));
	}, [campaigns, query]);

	// A campaign arriving via ?assignCampaignUuid= is only preselected when it can actually be used.
	const selected = useMemo(
		() => campaigns.find((c) => c.id === selectedId && c.isValid) ?? null,
		[campaigns, selectedId]
	);

	// campaignId -> { totalLeads, contacted } for the per-card progress label. Campaigns with no
	// assigned leads are absent here and fall back to the "no leads assigned yet" state.
	const statsByCampaign = useMemo(() => {
		const map: Record<string, { totalLeads: number; contacted: number }> = {};

		campaignStats?.data?.forEach((stat) => {
			map[stat.campaignId] = stat;
		});

		return map;
	}, [campaignStats]);

	// Feed the reused campaign-editor senders panel: the campaign's step channels (to grey out
	// ineligible users) and its default sender config merged with the available accounts.
	const steps = useMemo(() => getCampaignStepChannels(selected?.initialNodes), [selected]);
	const currentSenders = useMemo(
		() => computeCurrentSenders(sendingUsers?.data, selected?.senders),
		[sendingUsers?.data, selected?.senders]
	);
	const effectiveSenders = overrideSenders ?? currentSenders;

	// "Already connected on LinkedIn" for the picked campaign (PREC-4426).
	const connections = useAlreadyConnectedCheck(
		target,
		selected,
		changeSenders ? effectiveSenders : undefined
	);
	const countedButton = connections.notice.kind === "senderBulk" && connections.skipConnected;
	const addCount = leadsToAdd(
		reachOutTargetCount(target),
		connections.notice,
		connections.skipConnected
	);

	const {
		busy,
		handleAddAndSend,
		handleAssignToCampaign,
		handleCreateSnippets,
		activationModal,
		overrideModal,
		senderConfirmModal,
	} = useReachOutCampaignCommit({
		target,
		selected,
		effectiveSenders,
		changeSenders,
		onDone,
		skipConnectedToUserIds: connections.skipConnectedToUserIds,
	});

	const pickCampaign = (campaign: PickerCampaign) => {
		setSelectedId(campaign.id);
		setChangeSenders(false);
		setOverrideSenders(null);
	};

	const clearSelection = () => {
		setSelectedId(null);
		setChangeSenders(false);
		setOverrideSenders(null);
	};

	// Shared SenderAvatars: one bubble per distinct ACTIVE sending user (the raw Sender[] holds one
	// entry per user × channel — LinkedIn + each email — which showed the same person 2-3×).
	const renderSenders = (senders?: Sender[]) => (
		<div className="min-w-[88px]">
			<SenderAvatars senders={senders} />
		</div>
	);

	// Second-row progress label ("240/400 contacted (60%)" / "120 assigned" / "No leads assigned yet"),
	// bold leading number + muted remainder — see reachOutCampaignStat.
	const renderStat = (campaignId: string) => {
		const { emphasis, label } = formatReachOutCampaignStat(statsByCampaign[campaignId], t);

		return (
			<span className="whitespace-nowrap text-[11.5px] text-text-meta">
				{emphasis && <b className="font-bold text-brand-black">{emphasis}</b>}
				{label}
			</span>
		);
	};

	// What to tell the user about a campaign they cannot pick. `isValid` is a snapshot the builder
	// writes, so it can be stale — when the rules find nothing wrong, the only useful instruction is
	// to open the campaign once and let it refresh itself.
	const reasonFor = (campaignId: string): string => {
		const state = validation[campaignId];

		if (state?.needsRefresh) {
			return t("list.reachOut.picker.needsRefresh");
		}

		return state?.errors[0]?.message ?? t("list.reachOut.picker.setupIncomplete");
	};

	const renderDisabledCampaign = (campaign: PickerCampaign) => (
		<div
			key={campaign.id}
			className="flex flex-col gap-1.5 rounded-lg border border-border-light bg-brand-white p-2"
			data-testid="reach-out-campaign-disabled"
		>
			<div className="flex items-center gap-2">
				<span className="h-4 w-4 shrink-0 rounded-full border-2 border-status-disabled" />
				<span className="truncate text-xs font-bold text-text-meta">{campaign.name}</span>
				<span className="ml-auto shrink-0 rounded-md bg-status-warning-light px-1.5 py-0.5 text-[10px] font-semibold text-status-warning">
					{t("list.reachOut.picker.setupIncomplete")}
				</span>
			</div>
			<p className="pl-6 text-[11px] leading-snug text-text-meta">{reasonFor(campaign.id)}</p>
			{listId ? (
				<Link
					className="pl-6 text-[11px] font-semibold text-blue-action hover:underline"
					to={buildCampaignBuilderPath(listId, { name: campaign.name, campaignId: campaign.id })}
				>
					{t("list.reachOut.picker.openToFix")}
				</Link>
			) : null}
		</div>
	);

	const snippetBadge = (
		<span className="ml-auto inline-flex items-center gap-1 rounded-md bg-blue-action-200 px-1.5 py-0.5 text-[10px] font-semibold text-blue-action">
			<Icon className="h-2.5 w-2.5" icon={faSparkles} />
			{t("list.reachOut.picker.aiSnippetsBadge")}
		</span>
	);

	return (
		<div className="flex flex-col gap-2 px-1 pb-1" onClick={(e) => e.stopPropagation()}>
			{/* Inside the stopPropagation wrapper: the dialog is portalled, but React still bubbles its
			    clicks through this tree, and they must not reach the table row behind the menu. */}
			{activationModal}
			{overrideModal}
			{senderConfirmModal}
			{!selected ? (
				<>
					<div className="relative">
						<input
							autoFocus
							className="input-styles h-9 w-full rounded-lg pl-3 pr-8 text-xs"
							placeholder={t("list.reachOut.picker.searchPlaceholder")}
							value={query}
							onChange={(e) => setQuery(e.target.value)}
						/>
						<Icon
							className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-meta"
							icon={faMagnifyingGlass}
						/>
					</div>

					<div className="flex max-h-[240px] flex-col gap-1.5 overflow-y-auto">
						{filtered.length === 0 ? (
							<div className="px-2 py-4 text-center text-xs text-text-meta">
								{t("list.reachOut.picker.noResults")}
							</div>
						) : (
							filtered.map((campaign) =>
								!campaign.isValid ? (
									renderDisabledCampaign(campaign)
								) : (
									<button
										key={campaign.id}
										className="flex cursor-pointer flex-col gap-1.5 rounded-lg border border-border-light p-2 text-left hover:border-blue-action"
										type="button"
										onClick={() => pickCampaign(campaign)}
									>
										<div className="flex items-center gap-2">
											<span className="h-4 w-4 shrink-0 rounded-full border-2 border-status-disabled" />
											<span className="truncate text-xs font-bold text-brand-black">
												{campaign.name}
											</span>
											{campaign.usesSnippets && snippetBadge}
										</div>
										<div className="flex items-center gap-2.5 pl-6">
											{renderSenders(campaign.senders)}
											{renderStat(campaign.id)}
										</div>
									</button>
								)
							)
						)}
					</div>
				</>
			) : (
				<>
					<div className="text-[10.5px] font-semibold uppercase tracking-wide text-text-meta">
						{t("list.reachOut.picker.selectedLabel")}
					</div>
					<div className="relative rounded-lg border border-blue-action p-2">
						<div className="flex items-center gap-2 pr-8">
							<span className="truncate text-xs font-bold text-brand-black">{selected.name}</span>
							{selected.usesSnippets && snippetBadge}
						</div>
						<div className="mt-1.5 flex items-center gap-2.5">
							{renderSenders(effectiveSenders)}
							{renderStat(selected.id)}
						</div>
						<div className="absolute right-2 top-2 flex items-center gap-1">
							<button
								aria-label={t("list.reachOut.picker.clear")}
								className="flex h-5 w-5 cursor-pointer items-center justify-center rounded-md text-text-meta hover:bg-status-error-light hover:text-status-error"
								type="button"
								onClick={clearSelection}
							>
								<Icon className="h-3.5 w-3.5" icon={faXmark} />
							</button>
						</div>
					</div>

					<AlreadyConnectedNoticeView check={connections} />

					<label className="mt-1 flex cursor-pointer items-center gap-2 text-xs font-semibold text-brand-black">
						<span
							className={classNames(
								"relative h-5 w-9 shrink-0 rounded-full transition-colors",
								changeSenders ? "bg-blue-action" : "bg-status-disabled"
							)}
							data-testid="reach-out-change-senders"
							onClick={() => setChangeSenders((v) => !v)}
						>
							<span
								className={classNames(
									"absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform",
									changeSenders ? "left-0.5 translate-x-3.5" : "left-0.5"
								)}
							/>
						</span>
						{t("list.reachOut.picker.changeSenders")}
					</label>

					{changeSenders && (
						<div className="overflow-x-auto rounded-lg border border-border-light p-2">
							<SenderSelector
								sendingUsers={sendingUsers?.data ?? []}
								senders={effectiveSenders}
								setSenders={setOverrideSenders}
								steps={steps}
							/>
						</div>
					)}

					<div className="mt-1 flex flex-col gap-2">
						{selected.usesSnippets && (
							<button
								className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-border-light px-2 py-2 text-xs font-semibold text-brand-black hover:bg-brand-white disabled:cursor-not-allowed disabled:opacity-60"
								disabled={busy}
								type="button"
								onClick={handleCreateSnippets}
							>
								<Icon className="h-3.5 w-3.5 text-blue-action" icon={faSparkles} />
								{t("list.reachOut.picker.createSnippets")}
							</button>
						)}
						<button
							className="flex w-full cursor-pointer items-center justify-center gap-1.5 rounded-lg bg-blue-action px-2 py-2 text-xs font-semibold text-white hover:bg-blue-action-dark disabled:cursor-not-allowed disabled:opacity-60"
							disabled={busy}
							type="button"
							onClick={variant === "assign" ? handleAssignToCampaign : handleAddAndSend}
						>
							<Icon
								className="h-3.5 w-3.5"
								icon={variant === "assign" ? faClipboardCheck : faPaperPlane}
							/>
							{variant === "assign"
								? countedButton
									? t("list.reachOut.alreadyConnected.assignCount", { count: addCount })
									: t("list.reachOut.picker.assignToCampaign")
								: countedButton
									? t("list.reachOut.alreadyConnected.addCountAndSend", { count: addCount })
									: t("list.reachOut.picker.addAndSend")}
						</button>
					</div>
				</>
			)}
		</div>
	);
};
